import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Paper,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import * as timeEntriesAPI from '../../services/timeEntriesAPI';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

interface TimeEntry {
  id: number;
  project_id: number;
  user_id: number;
  start_time: string;
  end_time: string;
  break_duration: number;
  description: string;
  date: string;
  start_latitude?: number;
  start_longitude?: number;
  end_latitude?: number;
  end_longitude?: number;
  created_at: string;
  updated_at: string;
  project_name?: string;
  user_name?: string;
}

// Google Maps Wrapper Component
const GoogleMapComponent: React.FC<{
  timeEntry: TimeEntry;
  onMapLoad: () => void;
}> = ({ timeEntry, onMapLoad }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps || !timeEntry) {
      return;
    }

    try {
      // Clean up existing markers
      markersRef.current.forEach(marker => {
        marker.setMap(null);
      });
      markersRef.current = [];

      // Create map
      const map = new window.google.maps.Map(mapRef.current, {
        zoom: 15,
        center: { lat: 49.4949, lng: 10.8059 },
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: true,
      });

      mapInstanceRef.current = map;

      const bounds = new window.google.maps.LatLngBounds();
      let hasMarkers = false;

      // Add start marker
      if (timeEntry.start_latitude && timeEntry.start_longitude) {
        const startPosition = {
          lat: timeEntry.start_latitude,
          lng: timeEntry.start_longitude
        };

        const startMarker = new window.google.maps.Marker({
          position: startPosition,
          map: map,
          title: 'Start Position',
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });

        const startInfoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px;">
              <h4 style="margin: 0 0 8px 0; color: #2e7d32;">Start Position</h4>
              <p style="margin: 4px 0;"><strong>Zeit:</strong> ${format(new Date(timeEntry.start_time), 'HH:mm:ss', { locale: de })}</p>
              <p style="margin: 4px 0;"><strong>Koordinaten:</strong><br/>
              Lat: ${timeEntry.start_latitude.toFixed(6)}<br/>
              Lng: ${timeEntry.start_longitude.toFixed(6)}</p>
            </div>
          `
        });

        startMarker.addListener('click', () => {
          startInfoWindow.open(map, startMarker);
        });

        markersRef.current.push(startMarker);
        bounds.extend(startPosition);
        hasMarkers = true;
      }

      // Add end marker
      if (timeEntry.end_latitude && timeEntry.end_longitude) {
        const endPosition = {
          lat: timeEntry.end_latitude,
          lng: timeEntry.end_longitude
        };

        const endMarker = new window.google.maps.Marker({
          position: endPosition,
          map: map,
          title: 'End Position',
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });

        const endInfoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px;">
              <h4 style="margin: 0 0 8px 0; color: #d32f2f;">End Position</h4>
              <p style="margin: 4px 0;"><strong>Zeit:</strong> ${format(new Date(timeEntry.end_time), 'HH:mm:ss', { locale: de })}</p>
              <p style="margin: 4px 0;"><strong>Koordinaten:</strong><br/>
              Lat: ${timeEntry.end_latitude.toFixed(6)}<br/>
              Lng: ${timeEntry.end_longitude.toFixed(6)}</p>
            </div>
          `
        });

        endMarker.addListener('click', () => {
          endInfoWindow.open(map, endMarker);
        });

        markersRef.current.push(endMarker);
        bounds.extend(endPosition);
        hasMarkers = true;
      }

      // Draw path between markers if both exist
      if (markersRef.current.length === 2 && timeEntry.start_latitude && timeEntry.start_longitude && timeEntry.end_latitude && timeEntry.end_longitude) {
        const path = new window.google.maps.Polyline({
          path: [
            { lat: timeEntry.start_latitude, lng: timeEntry.start_longitude },
            { lat: timeEntry.end_latitude, lng: timeEntry.end_longitude }
          ],
          geodesic: true,
          strokeColor: '#1976d2',
          strokeOpacity: 0.8,
          strokeWeight: 3
        });
        path.setMap(map);
      }

      // Fit bounds or center map
      if (hasMarkers) {
        if (markersRef.current.length === 1) {
          map.setCenter(markersRef.current[0].getPosition()!);
          map.setZoom(16);
        } else {
          map.fitBounds(bounds);
          // Ensure minimum zoom level
          const listener = window.google.maps.event.addListener(map, 'bounds_changed', () => {
            if (map.getZoom()! > 18) map.setZoom(18);
            window.google.maps.event.removeListener(listener);
          });
        }
      }

      onMapLoad();
    } catch (error) {
      console.error('Error initializing Google Map:', error);
    }
  }, [timeEntry, onMapLoad]);

  useEffect(() => {
    // Wait for Google Maps API to be available
    const checkGoogleMaps = () => {
      if (window.google?.maps) {
        initializeMap();
      } else {
        setTimeout(checkGoogleMaps, 100);
      }
    };

    const timer = setTimeout(checkGoogleMaps, 50);
    return () => clearTimeout(timer);
  }, [initializeMap]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        markersRef.current.forEach(marker => {
          marker.setMap(null);
        });
        markersRef.current = [];
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height: '400px',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    />
  );
};

const TimeTrackingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [timeEntry, setTimeEntry] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);

  const fetchTimeEntry = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const entry = await timeEntriesAPI.getEntry(parseInt(id, 10));
      setTimeEntry(entry);
    } catch (error) {
      console.error('Error fetching time entry:', error);
      enqueueSnackbar('Fehler beim Laden des Zeiteintrags', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  const handleMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);

  useEffect(() => {
    fetchTimeEntry();
  }, [fetchTimeEntry]);

  const canEditTimeEntries = user?.role === 'admin';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!timeEntry) {
    return (
      <Box>
        <Typography>Zeiteintrag nicht gefunden</Typography>
      </Box>
    );
  }

  const duration = new Date(timeEntry.end_time).getTime() - new Date(timeEntry.start_time).getTime();
  const hours = Math.floor(duration / (1000 * 60 * 60));
  const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
  const breakHours = Math.floor(timeEntry.break_duration / 60);
  const breakMinutes = Math.floor(timeEntry.break_duration % 60);
  const netDuration = duration - (timeEntry.break_duration * 60 * 1000);
  const netHours = Math.floor(netDuration / (1000 * 60 * 60));
  const netMinutes = Math.floor((netDuration % (1000 * 60 * 60)) / (1000 * 60));

  const hasGPSData = timeEntry.start_latitude || timeEntry.end_latitude;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>
          Zeiteintrag Details
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/time-tracking')}
            sx={{ mr: 2 }}
          >
            Zurück
          </Button>
          {canEditTimeEntries && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/time-tracking/${id}/edit`)}
            >
              Bearbeiten
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={hasGPSData ? 6 : 12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Allgemeine Informationen
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Projekt
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {timeEntry.project_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Mitarbeiter
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {timeEntry.user_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Datum
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {format(new Date(timeEntry.date), 'dd.MM.yyyy', { locale: de })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Arbeitszeit
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {format(new Date(timeEntry.start_time), 'HH:mm', { locale: de })} - 
                    {format(new Date(timeEntry.end_time), 'HH:mm', { locale: de })}
                  </Typography>
                </Grid>
              </Grid>

              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Zeitauswertung
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Bruttozeit
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {hours}h {minutes}m
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Pausenzeit
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {breakHours}h {breakMinutes}m
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
                      <Typography variant="subtitle2">
                        Nettozeit
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {netHours}h {netMinutes}m
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {timeEntry.description && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Beschreibung
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {timeEntry.description}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {hasGPSData && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <LocationOnIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    GPS Standorte
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 2, position: 'relative' }}>
                  {!mapLoaded && (
                    <Box 
                      display="flex" 
                      justifyContent="center" 
                      alignItems="center" 
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 1,
                        height: '400px'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  <GoogleMapComponent 
                    timeEntry={timeEntry} 
                    onMapLoad={handleMapLoad}
                  />
                </Box>

                <Grid container spacing={2}>
                  {timeEntry.start_latitude && (
                    <Grid item xs={12} sm={6}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Start</strong>
                        </Typography>
                        <Typography variant="body2">
                          {format(new Date(timeEntry.start_time), 'HH:mm:ss', { locale: de })}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Lat: {timeEntry.start_latitude.toFixed(6)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Lng: {timeEntry.start_longitude?.toFixed(6)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {timeEntry.end_latitude && (
                    <Grid item xs={12} sm={6}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Ende</strong>
                        </Typography>
                        <Typography variant="body2">
                          {format(new Date(timeEntry.end_time), 'HH:mm:ss', { locale: de })}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Lat: {timeEntry.end_latitude.toFixed(6)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Lng: {timeEntry.end_longitude?.toFixed(6)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Box mt={3}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Erstellt am: {format(new Date(timeEntry.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Zuletzt geändert: {format(new Date(timeEntry.updated_at), 'dd.MM.yyyy HH:mm', { locale: de })}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default TimeTrackingDetail;